import { useRef } from 'react';
import { HOME } from '@/data/home';
import { HOME_INNOVATIONS, type InnovationCard } from '@/data/innovations';
import { Link } from 'react-router';
import { Reveal, useInView } from '@components/motion';
import FigmaFrame from './FigmaFrame';

const PLAY_BUTTON = '/landing/innovations/play-button.svg';
const CONTRACT_CARD = '/landing/innovations/contract-card.webp';
const AID_PHOTO = '/landing/innovations/aid-photo.webp';

/** Decorative vertical "waveform" of thin rounded bars beside the card copy. */
const WAVEFORM_BARS: ReadonlyArray<readonly [number, number]> = [
    [0, 0],
    [16, 4],
    [32, 45.4],
    [48, 49.4],
    [64.1, 66.7],
    [80.1, 66.7],
    [96.1, 66.7],
    [112.1, 22.7],
    [128.1, 4],
    [144.1, 0],
    [160.2, 4],
    [176.2, 12],
    [192.2, 22.7],
    [208.2, 26.7],
    [224.2, 45.4],
    [240.2, 49.4],
    [256.2, 56.1],
];

function Waveform({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <div className={`absolute left-[700px] top-[72px] h-[390px] w-[259px] ${className}`} style={style}>
            {WAVEFORM_BARS.map(([x, y], i) => (
                <div
                    key={i}
                    className="absolute w-[2.67px] rounded-full"
                    style={{
                        left: x,
                        top: y,
                        height: i === 0 ? 303 : 323,
                        background:
                            'linear-gradient(to bottom, rgba(133,133,133,0) 0%, rgba(124,124,124,0.79) 47%, rgba(115,115,115,0) 100%)',
                    }}
                />
            ))}
        </div>
    );
}

/** A card as it renders — backgrounds alternate white / #FFF7F2 down the stack. */
type Innovation = InnovationCard & { bg: string };

/** The CMS's homepage products, read at render so the hydrated list is what shows. */
const cards = (): Innovation[] =>
    HOME_INNOVATIONS.map((card, i) => ({ ...card, bg: i % 2 === 0 ? '#FFFFFF' : '#FFF7F2' }));

const CARD_H = 534;
/** Design-px height of the pinned heading band; cards pin just below it. */
const HEAD_H = 276;
/** Shared sticky top: every card pins in the SAME frame (just below the heading),
    so each new card slides up and covers the previous one exactly in place. */
const STACK_TOP = `calc(${((HEAD_H / 1920) * 100).toFixed(4)}vw + 8px)`;

/** Shared transition for card-content reveals. */
const RV_BASE = 'transition-[opacity,translate] duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)]';
const at = (ms: number): React.CSSProperties => ({ transitionDelay: `${ms}ms` });

/** The copy + mockup that sits ON one tinted card surface, revealed with a stagger. */
function InnovationCardContent({ innovation, shown }: { innovation: Innovation; shown: boolean }) {
    const rv = shown ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0';
    return (
        <>
            <img
                src={innovation.logo}
                alt=""
                className={`absolute left-[60px] top-[108px] size-[74px] object-contain ${RV_BASE} ${rv}`}
                style={at(0)}
            />
            <p
                className={`absolute left-[146px] top-[100px] flex h-[89px] items-center whitespace-nowrap text-[48px] font-semibold tracking-[-1px] text-[#151515] ${RV_BASE} ${rv}`}
                style={at(70)}
            >
                {innovation.name}
                <span className="text-[#8d8d8d]">{innovation.accent}</span>
            </p>
            <p
                className={`absolute left-[67px] top-[219px] w-[556px] text-[16px] font-normal leading-[24px] text-[#151515] ${RV_BASE} ${rv}`}
                style={at(150)}
            >
                {innovation.paragraph}
            </p>
            <Waveform className={`${RV_BASE} ${shown ? 'opacity-100' : 'opacity-0'}`} style={at(260)} />
            <Link
                to={innovation.href}
                className={`absolute left-[67px] top-[340px] flex h-[53px] w-[262px] items-center justify-center whitespace-nowrap rounded-[75px] bg-[#151515] text-[16px] font-bold text-white ${RV_BASE} ${rv}`}
                style={at(220)}
            >
                {innovation.cta}
            </Link>
            <img
                src={PLAY_BUTTON}
                alt=""
                className={`absolute left-[350px] top-[350px] size-[33px] ${RV_BASE} ${rv}`}
                style={at(280)}
            />
            <img
                src={CONTRACT_CARD}
                alt="Contract management interface"
                className={`absolute left-[986px] top-[62px] h-[410px] w-[563px] ${RV_BASE} ${rv}`}
                style={at(300)}
            />
        </>
    );
}

/** One sticky card in the stack; its content stagger-reveals when it scrolls in. */
function StackedCard({ innovation, index, last }: { innovation: Innovation; index: number; last: boolean }) {
    const ref = useRef<HTMLDivElement>(null);
    const shown = useInView(ref, { threshold: 0.35, once: true });
    return (
        <div
            ref={ref}
            className="sticky"
            style={{
                top: STACK_TOP,
                zIndex: index + 1,
                marginBottom: last ? 0 : '6vh',
            }}
        >
            <FigmaFrame designWidth={1920} designHeight={CARD_H} className="w-full">
                <div className="relative w-[1920px]" style={{ height: CARD_H }}>
                    <div
                        className="absolute left-[157px] top-0 h-[534px] w-[1609px] overflow-hidden rounded-[40px]"
                        style={{ backgroundColor: innovation.bg }}
                    >
                        <InnovationCardContent innovation={innovation} shown={shown} />
                    </div>
                </div>
            </FigmaFrame>
        </div>
    );
}

function InnovationsDesktop({ list }: { list: Innovation[] }) {
    return (
        <section className="bg-[#e5e5e5]">
            {/* Heading band — pinned to the top so the title and subtitle stay
                visible while the cards stack in the space beneath it. */}
            <div className="sticky top-0 z-20 bg-[#e5e5e5]">
                <FigmaFrame designWidth={1920} designHeight={HEAD_H} className="w-full">
                    <div className="relative h-[276px] w-[1920px] bg-[#e5e5e5]">
                        <Reveal
                            as="p"
                            once
                            className="absolute left-[154px] top-[135px] flex h-[103px] items-center whitespace-nowrap text-[96px] font-extrabold leading-[103px]"
                        >
                            <span className="font-normal text-[#6c6c6c]">Our&nbsp;</span>
                            <span className="text-[#1b1b1b]">Innovations</span>
                        </Reveal>
                        <Reveal
                            as="p"
                            once
                            delay={120}
                            className="absolute left-[924px] top-[148px] w-[621px] text-[16px] font-normal leading-[24px] text-[#151515]"
                        >
                            {HOME.innovations.intro}
                        </Reveal>
                    </div>
                </FigmaFrame>
            </div>

            {/* Stacking cards: each pins just below the heading and the next slides
                up and stacks over it — all inside the space under the title. */}
            <div className="relative">
                {list.map((innovation, i) => (
                    <StackedCard key={innovation.slug} innovation={innovation} index={i} last={i === list.length - 1} />
                ))}
            </div>

            {/* Trailing runway so the completed stack settles before the next section */}
            <div className="flex h-[16vh] items-center justify-center">
                <Link
                    to="/innovations"
                    className="inline-flex items-center gap-3 rounded-[10px] bg-[#1b1b1b] px-8 py-4 text-[16px] font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
                >
                    View All Innovations
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path
                            d="M5 12h13M12 5l7 7-7 7"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Link>
            </div>
        </section>
    );
}

/** One sticky mobile card; its content stagger-reveals as the card scrolls in. */
function StackedCardMobile({ innovation, index, last }: { innovation: Innovation; index: number; last: boolean }) {
    const ref = useRef<HTMLDivElement>(null);
    const shown = useInView(ref, { threshold: 0.35, once: true });
    const rv = shown ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0';

    return (
        <div
            ref={ref}
            className="sticky"
            style={{
                top: 'calc(var(--nav-height) + 12px)',
                zIndex: index + 1,
                marginBottom: last ? 0 : '4vh',
            }}
        >
            {/* Nearly a full screen tall, so the next card stays hidden until it
                scrolls up and stacks over this one. */}
            <div
                className="flex flex-col rounded-[21px] p-[19px]"
                style={{
                    backgroundColor: innovation.bg,
                    minHeight: 'calc(100dvh - var(--nav-height) - 34px)',
                }}
            >
                <div className={`flex items-center gap-[11px] ${RV_BASE} ${rv}`} style={at(0)}>
                    <img src={innovation.logo} alt="" className="size-[35px] shrink-0 object-contain" />
                    <p className="text-[24px] font-semibold leading-[32px] tracking-[-1px] text-[#151515]">
                        {innovation.name}
                        <span className="text-[#8d8d8d]">{innovation.accent}</span>
                    </p>
                </div>
                <img
                    src={AID_PHOTO}
                    alt=""
                    className={`mt-[23px] min-h-[131px] w-full flex-1 rounded-[6px] object-cover ${RV_BASE} ${rv}`}
                    style={at(120)}
                />
                <p className={`mt-[19px] text-[14px] leading-[24px] text-[#151515] ${RV_BASE} ${rv}`} style={at(200)}>
                    {innovation.paragraph}
                </p>
                <Link
                    to={innovation.href}
                    className={`mt-[20px] flex h-[53px] shrink-0 items-center justify-center rounded-[75px] bg-[#151515] px-[30px] text-[16px] font-bold text-white ${RV_BASE} ${rv}`}
                    style={at(280)}
                >
                    {innovation.cta}
                </Link>
            </div>
        </div>
    );
}

function InnovationsMobile({ list }: { list: Innovation[] }) {
    return (
        <section className="bg-[#e5e5e5] px-[23px] pb-[10vh] pt-12">
            <h2 className="text-[40px] font-extrabold leading-[50px]">
                <span className="font-normal text-[#6c6c6c]">Our </span>
                <span className="text-[#1b1b1b]">Innovations</span>
            </h2>
            <p className="mt-4 text-[14px] leading-[24px] text-[#333]">{HOME.innovations.intro}</p>

            {/* Stacking cards: each pins below the heading and the next covers it */}
            <div className="relative mt-6">
                {list.map((innovation, i) => (
                    <StackedCardMobile
                        key={innovation.slug}
                        innovation={innovation}
                        index={i}
                        last={i === list.length - 1}
                    />
                ))}
            </div>

            <Link
                to="/innovations"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#1b1b1b] px-5 py-[14px] text-[16px] font-bold text-white"
            >
                View All Innovations
            </Link>
        </section>
    );
}

export default function Innovations() {
    const list = cards();
    if (!list.length) return null;
    return (
        <>
            <div className="hidden lg:block">
                <InnovationsDesktop list={list} />
            </div>
            <div className="lg:hidden">
                <InnovationsMobile list={list} />
            </div>
        </>
    );
}
